import { useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import useSWR from 'swr';
import Select from '@/components/@IntranetLibrary/Select';
import Spinner from '@/components/@IntranetLibrary/Spinner';
import { approvalOutlineSelectSx } from '@/components/Approval/@components/ApprovalFormTable';
import {
  buildProductionVendorContentHtml,
  ProductionVendorSnapshot,
} from '@/components/Approval/@components/ProductionVendor';
import { getProductionVendorDraftSourceUrl } from '@/constant/approval';
import { useSnackbar } from '@/hooks/global-tools';
import { getNestedDataFetcher } from '@/utils/apis';
import {
  buildProductionVendorCandidateLabel,
  type ProductionVendorCandidate,
  type ProductionVendorDraftSource,
} from '@/utils/approval/productionVendor';
import { FieldBlock } from './styles';
import type { ApprovalFormValues } from './types';

const CONTROL_HEIGHT = 44;

type Props = { isEditMode: boolean };

/**
 * GERP 생산거래처 신규/변경(120) — 미상신 건을 고르면 바로 본문을 불러옴.
 * 편집은 건을 바꿀 수 없고 저장 본문만
 */
const ProductionVendorField = ({ isEditMode }: Props) => {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const { control, setValue } = useFormContext<ApprovalFormValues>();
  const loaded = useWatch({ control, name: 'productionVendor' });

  const { data: source, isLoading } = useSWR<ProductionVendorDraftSource>(
    isEditMode ? null : getProductionVendorDraftSourceUrl(),
    getNestedDataFetcher,
    {
      revalidateOnFocus: false,
      onError: () =>
        snackbar.showError({ message: t('데이터를 불러오지 못했습니다.') }),
    },
  );
  const candidates = source?.display.candidates ?? [];

  const [selectedId, setSelectedId] = useState('');
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  // 불러오는 중 다른 건을 고르면 늦게 온 이전 응답이 본문을 덮지 않게
  const requestIdRef = useRef(0);

  const loadContent = async (option: ProductionVendorCandidate) => {
    setSelectedId(option.aprvId);
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    setIsLoadingContent(true);
    try {
      const data = await getNestedDataFetcher<ProductionVendorDraftSource>(
        getProductionVendorDraftSourceUrl(option.aprvId),
      );
      if (requestId !== requestIdRef.current) return;
      if (!data.display.content) throw new Error('content missing');
      setValue(
        'productionVendor',
        {
          systemId: option.sysId,
          approvalTypeCode: option.aprvTypcd,
          approvalId: option.aprvId,
          content: buildProductionVendorContentHtml(data.display.content, t),
        },
        { shouldDirty: true },
      );
    } catch {
      if (requestId !== requestIdRef.current) return;
      // 이전 건 본문이 남아 그 건으로 상신되지 않게 본문도 비움. 같은 건을 다시 골라 재시도 가능
      setSelectedId('');
      setValue('productionVendor', null, { shouldDirty: true });
      snackbar.showError({ message: t('데이터를 불러오지 못했습니다.') });
    } finally {
      if (requestId === requestIdRef.current) setIsLoadingContent(false);
    }
  };

  const handleChange = (approvalId: string) => {
    const option = candidates.find((c) => c.aprvId === approvalId);
    if (option) loadContent(option);
  };

  const body = loaded?.content ? (
    <ProductionVendorSnapshot content={loaded.content} />
  ) : null;

  if (isEditMode) return <FieldBlock>{body}</FieldBlock>;

  return (
    <FieldBlock>
      <ControlRow>
        <Select
          value={selectedId}
          options={candidates.map((c) => ({
            value: c.aprvId,
            label: buildProductionVendorCandidateLabel(c),
          }))}
          placeholder={
            isLoading
              ? t('불러오는 중...')
              : candidates.length
              ? t('결재할 건을 선택해주세요')
              : t('진행할 내역이 없습니다.')
          }
          onChange={(e) => handleChange(String(e.target.value))}
          sx={{
            ...approvalOutlineSelectSx,
            flex: 1,
            minWidth: 0,
            height: CONTROL_HEIGHT,
          }}
        />
        {isLoadingContent && <Spinner theme="black" size="16px" />}
      </ControlRow>
      {body}
    </FieldBlock>
  );
};

const ControlRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 8,
});

export default ProductionVendorField;
